import { MutableRefObject } from "react"
import throttleWait from "./throttleWait"

const WHEEL_SPEED = 1000 / 100
const KEY_SPEED = 1000 / 32
const DELTA_FACTOR = 1.5

const DESKTOP_IMAGE_MAX_WIDTH = 642
const MOBILE_IMAGE_MAX_WIDTH = 345

const CONVERT_TO_JPG="&format=jpg&bgcolor=white&quality=75"

export type ImageData = {
  imageUrl: string
  alt: string
  srcSet?: string
}

export type Images360ManagerState = "idle" | "loading" | "error" | "loaded"

export const ImageSrc = (image: string, isDesktop: boolean) =>
  `${image}?width=${
    isDesktop ? DESKTOP_IMAGE_MAX_WIDTH : MOBILE_IMAGE_MAX_WIDTH
  }${CONVERT_TO_JPG}`

export const ImageSrcSet = (image: string, isDesktop: boolean) =>
  !isDesktop
    ? `${image}?width=${MOBILE_IMAGE_MAX_WIDTH}${CONVERT_TO_JPG} 1x, ${image}?width=${
        MOBILE_IMAGE_MAX_WIDTH * 2
      }${CONVERT_TO_JPG} 2x, ${image}?width=${MOBILE_IMAGE_MAX_WIDTH * 3}${CONVERT_TO_JPG} 3x`
    : undefined

export default class Images360Manager {
  private clientWidth: number = null
  private clientX: number = null
  private currentIndex: number = null
  private images: HTMLImageElement[] = null
  private synced = 0

  constructor(
    private ref: MutableRefObject<HTMLDivElement>,
    private setState: (state: Images360ManagerState) => void
  ) {}

  // Drag with mouse
  mousedownHandler = (e: MouseEvent) => {
    this.clientX = e.clientX
    this.clientWidth = this.ref.current.clientWidth
  }

  mousemoveHandler = (e: MouseEvent) => this.deltaX(e.clientX)

  // Drag by touch
  touchstartHandler = (e: TouchEvent) => {
    this.clientX = e.changedTouches[0].clientX
    this.clientWidth = this.ref.current.clientWidth
  }
  touchmoveHandler = (e: TouchEvent) => this.deltaX(e.changedTouches[0].clientX)

  // Disable dragging
  endHandler = () => (this.clientX = null)

  // Drag by mousewheel
  mousewheelHandler = (e: WheelEvent) => {
    if (e.deltaY <= -100) this.draw(WHEEL_SPEED, -1)
    if (e.deltaY >= 100) this.draw(WHEEL_SPEED, 1)
    e.preventDefault()
  }

  // Drag with arrow keys
  keydownHandler = (e: KeyboardEvent) => {
    if (e.key === "ArrowLeft") this.draw(KEY_SPEED, -1)
    if (e.key === "ArrowRight") this.draw(KEY_SPEED / 16, 1)
    e.preventDefault()
  }

  async load(data: ImageData[], isDesktop: boolean) {
    const lastImages = this.images
    const synced = ++this.synced
    if (data?.length > 0) {
      try {
        this.setState("loading")
        this.images = await this.loader(data, isDesktop)
        if (synced === this.synced) {
          this.detach(lastImages)
          this.images.forEach((image) => this.ref.current.append(image))
          this.draw(0)
          this.setState("loaded")
        }
      } catch (e) {
        if (synced === this.synced) {
          this.detach(lastImages)
          this.images = null
          this.setState("error")
        }
      }
    } else {
      this.detach(lastImages)
      this.images = null
      this.setState("idle")
    }
  }

  private detach(images: HTMLImageElement[]) {
    if (images != null && this.ref.current) {
      this.ref.current.innerHTML = ""
    }
  }

  private loader(images: ImageData[], isDesktop: boolean) {
    return Promise.all(
      images.map(
        ({ imageUrl, alt }) =>
          new Promise<HTMLImageElement>((resolve, reject) => {
            const encodedUrl = imageUrl
            const image = new Image()
            image.src = ImageSrc(encodedUrl, isDesktop)
            image.alt = alt
            if (!isDesktop) {
              image.srcset = ImageSrcSet(encodedUrl, isDesktop)
              image.sizes = "100vw"
            }
            image.classList.add("hidden")
            image.addEventListener("load", () => resolve(image))
            image.addEventListener("error", (e) => reject(e.message))
          })
      )
    )
  }

  private deltaX(clientX: number) {
    if (this.clientX != null && this.images != null) {
      const skip = this.clientWidth / this.images.length / DELTA_FACTOR
      const deltaX = clientX - this.clientX
      if (deltaX <= -skip) this.draw(0, -1)
      if (deltaX >= skip) this.draw(0, 1)
      if (Math.abs(deltaX) >= skip) this.clientX = clientX
    }
  }

  private draw = throttleWait((offset = 0) => {
    if (this.images !== null) {
      let index = this.currentIndex ?? 0
      this.images[index].classList.add("hidden")
      index += offset
      if (index < 0) index = this.images.length - 1
      if (index >= this.images.length) index = 0
      this.currentIndex = index
      this.images[index].classList.remove("hidden")
    }
  })

  init() {
    const current = this.ref.current
    if (current == null) return
    current.addEventListener("mousewheel", this.mousewheelHandler)
    current.addEventListener("mousedown", this.mousedownHandler)
    current.addEventListener("mousemove", this.mousemoveHandler)
    current.addEventListener("touchstart", this.touchstartHandler)
    current.addEventListener("touchmove", this.touchmoveHandler)
    current.addEventListener("touchend", this.endHandler)
    current.addEventListener("keydown", this.keydownHandler)
    window.addEventListener("mouseup", this.endHandler, { passive: false })
  }

  destroy() {
    const current = this.ref.current
    if (current == null) return
    this.detach(this.images)
    current.removeEventListener("mousewheel", this.mousewheelHandler)
    current.removeEventListener("mousedown", this.mousedownHandler)
    current.removeEventListener("mousemove", this.mousemoveHandler)
    current.removeEventListener("touchstart", this.touchstartHandler)
    current.removeEventListener("touchmove", this.touchmoveHandler)
    current.removeEventListener("touchend", this.endHandler)
    current.removeEventListener("keydown", this.keydownHandler)
    window.removeEventListener("mouseup", this.endHandler)
  }
}
